import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Stepper, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { defineStepper } from '@stepperize/react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import {
  NewsHighlightedContentForm,
  NewsHighlightedContentSchema,
} from '../../schemas/NewsHighlightedContentSchema'
import {
  NewsHighlightedImageForm,
  NewsHighlightedImageSchema,
} from '../../schemas/NewsHighlightedImageSchema'
import {
  NewsHighlightedSummaryForm,
  NewsHighlightedSummarySchema,
} from '../../schemas/NewsHighlightedSummarySchema'
import { NewsOrderForm, NewsOrderSchema } from '../../schemas/NewsOrderSchema'
import { useCreateNewsHighlighted } from '../hooks/useCreateNewsHighlighted'
import { FormNewsContent } from './FormNewsContent'
import { FormNewsImage } from './FormNewsImage'
import { FormNewsOrder } from './FormNewsOrder'
import { FormNewsSummary } from './FormNewsSummary'
import { NewsPreview } from './NewsPreview'

type Form = NewsHighlightedSummaryForm &
  NewsHighlightedContentForm &
  NewsHighlightedImageForm &
  NewsOrderForm

export function DrawerCreateNewsHighlighted() {
  const { useStepper, utils } = defineStepper(
    {
      id: 'summary',
      schema: NewsHighlightedSummarySchema(),
    },
    {
      id: 'content',
      schema: NewsHighlightedContentSchema(),
    },
    {
      id: 'crop',
      schema: NewsHighlightedImageSchema(),
    },
    {
      id: 'order',
      schema: NewsOrderSchema(),
    },
    {
      id: 'preview',
      schema: z.object({}),
    },
  )

  const { t } = useTranslation('panel')
  const stepper = useStepper()
  const drawerCreateNewsHighlighted = useDrawer(
    'drawer-create-news-highlighted',
  )
  const hookForm = useForm({
    resolver: zodResolver(stepper.current.schema),
  })
  const { handleCreateNewsHighlighted, isCreatingNewsHighlighted } =
    useCreateNewsHighlighted()

  const handleSubmit = () => {
    const data = hookForm.getValues() as Form

    if (stepper.isLast) {
      return handleCreateNewsHighlighted(data)
    }

    stepper.next()
  }

  const handleClose = () => {
    drawerCreateNewsHighlighted.close()
    hookForm.reset({
      title: '',
      tag: [],
      subtitle: '',
      postExternalUrl: '',
      postFile: undefined,
      content: '',
      image: undefined,
      postImage: undefined,
      sliderSorte: 0,
    })
    stepper.reset()
  }

  const CURRENT_STEP = utils.getIndex(stepper.current.id) + 1

  return (
    <Drawer.Root id="drawer-create-news-highlighted">
      <Drawer.Header className="w-full flex gap-4" onClose={handleClose}>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('news.drawer_create_news.header.title')}
        </h1>

        <Stepper.Root className="sm:min-w-sm mx-auto">
          <Stepper.StepNumber
            number={1}
            isCompleted={CURRENT_STEP > 1}
            isActive={stepper.current.id === 'summary'}
          />

          <Stepper.StepNumber
            number={2}
            isCompleted={CURRENT_STEP > 2}
            isActive={stepper.current.id === 'content'}
          />

          <Stepper.StepNumber
            number={3}
            isCompleted={CURRENT_STEP > 3}
            isActive={stepper.current.id === 'crop'}
          />

          <Stepper.StepNumber
            number={4}
            isCompleted={CURRENT_STEP > 4}
            isActive={stepper.current.id === 'preview'}
          />

          <Stepper.StepNumber
            number={5}
            isActive={stepper.current.id === 'order'}
          />
        </Stepper.Root>
      </Drawer.Header>

      <Drawer.Body>
        <FormProvider {...hookForm}>
          <form
            onSubmit={hookForm.handleSubmit(handleSubmit)}
            className="h-full"
          >
            {stepper.switch({
              summary: () => <FormNewsSummary />,
              crop: () => <FormNewsImage aspectRatio={6 / 1} />,
              content: () => (
                <FormNewsContent
                  aspectRatio="6 / 1"
                  sizes={{
                    ideal: '1400 × 400',
                    firstOption: '1050 × 300',
                    secondOption: '2100 × 600',
                  }}
                />
              ),
              order: () => <FormNewsOrder />,
              preview: () => <NewsPreview />,
            })}
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer className="flex gap-4 justify-end">
        {stepper.isFirst ? (
          <Button.Root variant="secondary" onClick={handleClose}>
            {t('news.drawer_create_news.footer.button_cancel')}
          </Button.Root>
        ) : (
          <Button.Root variant="secondary" onClick={() => stepper.prev()}>
            {t('news.drawer_create_news.footer.button_prev')}
          </Button.Root>
        )}

        <Button.Root
          onClick={hookForm.handleSubmit(handleSubmit)}
          isLoading={isCreatingNewsHighlighted}
        >
          {stepper.isLast
            ? t('news.drawer_create_news.footer.button_submit')
            : t('news.drawer_create_news.footer.button_next')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
